import React from 'react';
import axios from 'axios';
import { PluginInterface } from '../types/PluginInterface';
import { generateUniformPlaceholder } from '../utils/PlaceholderGenerator';

export interface UserPlugin {
  id: string;
  userId: number;
  name: string;
  pluginType: string;
  parentPluginType?: string;        // ID du plugin natif parent (si fork)
  description?: string;
  pluginCode: string;
  pluginFormat?: 'react' | 'html'; // 'react' (défaut, Babel) | 'html' (iframe, sans limite CDN)
  isExample?: boolean;              // marqué comme modèle few-shot pour l'IA
  defaultConfig?: any;
  documentationForAI?: any;
  createdAt: string;
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/** Classifie une erreur de plugin et tente une correction déterministe côté backend */
export const pluginFix = async (
  code: string,
  errorMessage: string,
): Promise<{ type: string; autoFixed: boolean; fixedCode?: string; llmPrompt?: string }> => {
  try {
    const { data } = await axios.post(
      '/api/ai/plugin-fix',
      { code, errorMessage },
      { headers: getAuthHeaders() },
    );
    return data;
  } catch {
    return { type: 'unknown', autoFixed: false };
  }
};

/** Enregistre un log de génération (non-bloquant, best-effort) */
export const logPluginGeneration = (entry: {
  pluginId?: string;
  pluginType?: string;
  userPrompt: string;
  generatedCode?: string;
  success: boolean;
  errorType?: string;
  errorMessage?: string;
  autoFixed?: boolean;
  retryCount?: number;
  model?: string;
  durationMs?: number;
  linesOfCode?: number;
}): void => {
  axios.post('/api/ai/plugin-log', entry, { headers: getAuthHeaders() }).catch(() => {});
};

export const fetchUserPlugins = async (): Promise<UserPlugin[]> => {
  try {
    const { data } = await axios.get('/api/user-plugins', { headers: getAuthHeaders() });
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
};

export const saveUserPlugin = async (
  plugin: Omit<UserPlugin, 'id' | 'userId' | 'createdAt'> & { pluginFormat?: 'react' | 'html' }
): Promise<UserPlugin> => {
  const { data } = await axios.post('/api/user-plugins', plugin, { headers: getAuthHeaders() });
  return data;
};

export const deleteUserPlugin = async (id: string): Promise<void> => {
  await axios.delete(`/api/user-plugins/${id}`, { headers: getAuthHeaders() });
};

export const updateUserPlugin = async (
  id: string,
  data: Partial<Pick<UserPlugin, 'name' | 'description' | 'pluginCode' | 'defaultConfig' | 'documentationForAI'>>
): Promise<UserPlugin> => {
  const { data: result } = await axios.patch(`/api/user-plugins/${id}`, data, { headers: getAuthHeaders() });
  return result;
};

export interface WebSearchResult {
  apps: Array<{ title: string; snippet: string }>;
  docs: Array<{ title: string; url: string; snippet: string }>;
  docPages: Array<{ title: string; url: string; content: string }>;
}

export const webSearchForPlugin = async (query: string): Promise<WebSearchResult> => {
  try {
    const { data } = await axios.post('/api/ai/web-search', { query }, { headers: getAuthHeaders() });
    return { apps: data.apps || [], docs: data.docs || [], docPages: data.docPages || [] };
  } catch {
    return { apps: [], docs: [], docPages: [] };
  }
};

export const toggleExampleUserPlugin = async (id: string): Promise<{ id: string; isExample: boolean }> => {
  const { data } = await axios.patch(`/api/user-plugins/${id}/toggle-example`, {}, { headers: getAuthHeaders() });
  return data;
};

export const installUserPlugin = async (id: string): Promise<{ installed: boolean; file?: string }> => {
  try {
    const { data } = await axios.post(`/api/user-plugins/${id}/install`, {}, { headers: getAuthHeaders() });
    return data;
  } catch {
    return { installed: false };
  }
};

/** Trouve le fork d'un plugin natif (par parentPluginType) */
export const findForkByNativeType = async (nativeType: string): Promise<UserPlugin | null> => {
  try {
    const { data } = await axios.get(`/api/user-plugins/fork/${encodeURIComponent(nativeType)}`, { headers: getAuthHeaders() });
    return data || null;
  } catch {
    return null;
  }
};

export interface PluginContextResult {
  contextType: 'fork' | 'native_skeleton' | 'fallback';
  code: string;
  pluginId?: string;
  pluginName?: string;
  pluginType?: string;
}

/** Récupère le contexte optimal pour modifier/créer un fork d'un plugin natif */
export const getPluginContext = async (nativePluginType: string): Promise<PluginContextResult> => {
  try {
    const { data } = await axios.post('/api/ai/plugin-context', { nativePluginType }, { headers: getAuthHeaders() });
    return data;
  } catch {
    return { contextType: 'fallback', code: '' };
  }
};

/** Analyse l'UI d'une image via olmOCR (port 8081) */
export const ocrPluginImage = async (imageUrl: string): Promise<string> => {
  try {
    const { data } = await axios.post('/api/ai/plugin-ocr', { imageUrl }, { headers: getAuthHeaders() });
    if (typeof data.uiDescription === 'object') return JSON.stringify(data.uiDescription, null, 2);
    return data.uiDescription || '';
  } catch {
    return '';
  }
};

export interface CrawlPluginResult {
  markdown: string;
  images: string[];
  colors: string[];
  structure: string;
  url: string;
}

/** Crawle une URL et extrait la structure UI pour générer un plugin */
export const crawlPluginUrl = async (url: string): Promise<CrawlPluginResult | null> => {
  try {
    const { data } = await axios.post('/api/ai/plugin-crawl', { url }, { headers: getAuthHeaders() });
    return data;
  } catch {
    return null;
  }
};

/** ConfigComponent générique basé sur les clés/types de defaultConfig. */
function buildGenericConfigComponent(defConfig: Record<string, any>) {
  const GenericConfig: React.FC<{ config: any; onConfigChange: (c: any) => void }> = ({ config, onConfigChange }) => {
    const cfg = { ...defConfig, ...config };
    const entries = Object.entries(cfg).filter(([k]) => k !== 'id');

    if (entries.length === 0) {
      return React.createElement('div', { className: 'p-3 text-muted' }, 'Aucun paramètre configurable.');
    }

    return React.createElement('div', { className: 'p-3' },
      ...entries.map(([key, defaultVal]) => {
        const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, (s: string) => s.toUpperCase());
        const val = cfg[key];

        if (typeof defaultVal === 'boolean') {
          return React.createElement('div', { key, className: 'mb-3 form-check' },
            React.createElement('input', {
              type: 'checkbox', className: 'form-check-input', id: `cfg-${key}`,
              checked: !!val,
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => onConfigChange({ ...cfg, [key]: e.target.checked }),
            }),
            React.createElement('label', { className: 'form-check-label', htmlFor: `cfg-${key}` }, label),
          );
        }

        if (typeof defaultVal === 'number') {
          return React.createElement('div', { key, className: 'mb-3' },
            React.createElement('label', { className: 'form-label fw-semibold' }, label),
            React.createElement('input', {
              type: 'number', className: 'form-control', value: val ?? '',
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => onConfigChange({ ...cfg, [key]: Number(e.target.value) }),
            }),
          );
        }

        if (typeof defaultVal === 'string' && (defaultVal.startsWith('#') || key.toLowerCase().includes('color'))) {
          return React.createElement('div', { key, className: 'mb-3' },
            React.createElement('label', { className: 'form-label fw-semibold' }, label),
            React.createElement('input', {
              type: 'color', className: 'form-control form-control-color', value: val || '#000000',
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => onConfigChange({ ...cfg, [key]: e.target.value }),
            }),
          );
        }

        if (typeof defaultVal === 'object' || Array.isArray(defaultVal)) {
          return React.createElement('div', { key, className: 'mb-3' },
            React.createElement('label', { className: 'form-label fw-semibold' }, `${label} (JSON)`),
            React.createElement('textarea', {
              className: 'form-control font-monospace', rows: 3,
              value: JSON.stringify(val, null, 2),
              onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                try { onConfigChange({ ...cfg, [key]: JSON.parse(e.target.value) }); } catch {}
              },
            }),
          );
        }

        const strVal = String(val ?? '');
        const isLong = strVal.includes('\n') || strVal.length > 80
          || ['content', 'html', 'text', 'description', 'body'].some(w => key.toLowerCase().includes(w));

        if (isLong) {
          return React.createElement('div', { key, className: 'mb-3' },
            React.createElement('label', { className: 'form-label fw-semibold' }, label),
            React.createElement('textarea', {
              className: 'form-control', rows: 4, value: strVal,
              onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => onConfigChange({ ...cfg, [key]: e.target.value }),
            }),
          );
        }

        return React.createElement('div', { key, className: 'mb-3' },
          React.createElement('label', { className: 'form-label fw-semibold' }, label),
          React.createElement('input', {
            className: 'form-control', value: strVal,
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => onConfigChange({ ...cfg, [key]: e.target.value }),
          }),
        );
      })
    );
  };
  GenericConfig.displayName = 'GenericConfig';
  return GenericConfig;
}

/** ConfigComponent minimal quand defaultConfig est vide. */
const ReadyToInsertConfig: React.FC<{ config: any; onConfigChange: (c: any) => void }> = () =>
  React.createElement('div', { className: 'p-3 text-muted small' },
    '✅ Ce plugin est prêt. Cliquez sur "Insérer" pour l\'ajouter à l\'onglet actif.'
  );
ReadyToInsertConfig.displayName = 'ReadyToInsertConfig';

/** Construit un PreviewComponent de repli pour un plugin utilisateur (avant rebuild frontend). */
function buildPreviewComponent(name: string, description: string, defConfig: Record<string, any>) {
  const entries = Object.entries(defConfig).filter(([k]) => k !== 'id').slice(0, 6);

  const Preview: React.FC<{ config: any }> = ({ config }) => {
    const cfg = { ...defConfig, ...config };
    const primary = (Object.entries(cfg).find(([k]) => k.toLowerCase().includes('color'))?.[1] as string) || '#6c757d';

    return React.createElement('div', {
      style: { border: `2px solid ${primary}`, borderRadius: 8, padding: 16, fontFamily: 'sans-serif' },
    },
      React.createElement('div', {
        style: { background: primary, color: '#fff', borderRadius: 6, padding: '8px 12px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 },
      },
        React.createElement('span', { style: { fontSize: 20 } }, '🔧'),
        React.createElement('strong', null, name),
      ),
      description && React.createElement('p', { style: { fontSize: 13, color: '#555', marginBottom: 12 } }, description),
      entries.map(([key, val]) => {
        const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, (s: string) => s.toUpperCase());
        const display = Array.isArray(val)
          ? `${val.length} élément(s)`
          : typeof val === 'object' ? JSON.stringify(val).slice(0, 60)
          : String(cfg[key] ?? val).slice(0, 80);
        return React.createElement('div', { key, style: { fontSize: 12, marginBottom: 4, display: 'flex', gap: 6 } },
          React.createElement('span', { style: { color: '#888', minWidth: 110 } }, label + ' :'),
          React.createElement('span', { style: { color: '#333', fontWeight: 500 } }, display),
        );
      }),
      React.createElement('div', { style: { marginTop: 10, fontSize: 11, color: '#aaa', textAlign: 'right' as const } },
        '🔧 Plugin personnalisé — rechargez après rebuild pour le rendu complet'
      ),
    );
  };
  Preview.displayName = `UserPluginPreview`;
  return Preview;
}

/**
 * Convertit un UserPlugin (BDD) en PluginInterface utilisable dans le registry.
 *
 * pluginFormat === 'html'  → iframe (CDN libre, aucun RUNTIME_MODULES)
 * pluginFormat === 'react' → Babel evaluation (si `evaluated` fourni) ou fallback générique
 */
export function userPluginToInterface(up: UserPlugin, evaluated?: any): PluginInterface {
  const defConfig = up.defaultConfig || {};
  const hasConfig = Object.keys(defConfig).filter(k => k !== 'id').length > 0;

  // ── Cas 0 : plugin HTML/iframe (nouveau format, le plus ouvert) ────────────
  if (up.pluginFormat === 'html') {
    const IframePreview: React.FC<{ config: any }> = ({ config }) => {
      const merged = { ...defConfig, ...config };
      const src = `/api/user-plugins/${up.id}/static?config=${encodeURIComponent(JSON.stringify(merged))}`;
      return React.createElement('iframe', {
        key: src, // recharge quand la config change
        src,
        style: {
          width: '100%',
          height: '500px',
          border: 'none',
          borderRadius: 8,
          display: 'block',
        } as React.CSSProperties,
        sandbox: 'allow-scripts allow-same-origin allow-forms allow-popups',
        title: up.name,
      });
    };
    IframePreview.displayName = `HTMLPlugin_${up.pluginType}`;

    return {
      metadata: {
        id: up.pluginType,
        name: up.name,
        description: up.description || up.name,
        version: '1.0.0',
        author: 'User',
        category: 'utility',
        icon: '🔧',
        tags: ['user', 'custom', 'html'],
      },
      documentationForAI: up.documentationForAI || {
        description: up.description || up.name,
        usage: `Plugin HTML "${up.name}"`,
        configSchema: {},
        exampleConfig: defConfig,
      },
      defaultConfig: defConfig,
      ConfigComponent: hasConfig ? buildGenericConfigComponent(defConfig) : ReadyToInsertConfig,
      PreviewComponent: IframePreview,
      generateHTML: (config: any) => generateUniformPlaceholder({
        title: up.name,
        description: up.description || '',
        icon: '🔧',
        category: 'utility',
        config: { ...defConfig, ...config },
        pluginId: config.id || `${up.pluginType}-${Date.now()}`,
        pluginType: up.pluginType,
      }),
    };
  }

  // ── Cas 1 : plugin React évalué avec succès ────────────────────────────────
  if (evaluated?.metadata?.id && evaluated?.PreviewComponent) {
    const resolvedConfig = evaluated.defaultConfig ?? defConfig;
    const resolvedHasConfig = Object.keys(resolvedConfig).filter((k: string) => k !== 'id').length > 0;
    return {
      ...evaluated,
      metadata: { ...evaluated.metadata, id: up.pluginType },
      defaultConfig: resolvedConfig,
      // ConfigComponent généré automatiquement si absent (wrapSimpleComponent ne le produit pas)
      ConfigComponent: evaluated.ConfigComponent
        ?? (resolvedHasConfig ? buildGenericConfigComponent(resolvedConfig) : ReadyToInsertConfig),
      generateHTML: evaluated.generateHTML ?? ((config: any) => generateUniformPlaceholder({
        title: up.name,
        description: up.description || '',
        icon: evaluated.metadata?.icon || '🔧',
        category: evaluated.metadata?.category || 'utility',
        config: { ...resolvedConfig, ...config },
        pluginId: config.id || `${up.pluginType}-${Date.now()}`,
        pluginType: up.pluginType,
      })),
    };
  }

  // ── Cas 2 : fallback générique (avant évaluation ou si échec) ──────────────
  return {
    metadata: {
      id: up.pluginType,
      name: up.name,
      description: up.description || up.name,
      version: '1.0.0',
      author: 'User',
      category: 'utility',
      icon: '🔧',
      tags: ['user', 'custom'],
    },
    documentationForAI: up.documentationForAI || {
      description: up.description || up.name,
      usage: `Plugin personnalisé "${up.name}"`,
      configSchema: {},
      exampleConfig: defConfig,
    },
    defaultConfig: defConfig,
    ConfigComponent: hasConfig ? buildGenericConfigComponent(defConfig) : ReadyToInsertConfig,
    PreviewComponent: buildPreviewComponent(up.name, up.description || '', defConfig),
    generateHTML: (config: any) => generateUniformPlaceholder({
      title: up.name,
      description: up.description || '',
      icon: '🔧',
      category: 'utility',
      config: { ...defConfig, ...config },
      pluginId: config.id || `${up.pluginType}-${Date.now()}`,
      pluginType: up.pluginType,
    }),
  };
}
